import type { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Skeleton, Tooltip } from '~/components';
import { cn } from '~/lib/cn';
import { isEnvEnabled } from '~/lib/env';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import {
  TableBody,
  TableCell,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import type {
  ArtistDetails,
  AssetFamily,
} from '~/modules/artist-roster/lib/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { PreReleaseSkeletonTable } from './PreReleaseSkeletonTable';
import type { PreReleaseTableMeta } from './PreReleaseTable';
import { PreReleaseTooltipContent } from './PreReleaseTooltipContent';

interface PreReleaseTableBodyProps {
  table: Table<AssetFamily>;
  meta: PreReleaseTableMeta;
  artistData: ArtistDetails;
  isFetchingTracks: boolean;
  isLoadingBenchmark: boolean;
}

const BENCHMARK_COLUMNS = [
  'firstWeekStreams',
  'secondWeekStreams',
  'firstYearStreams',
];

export function PreReleaseTableBody({
  table,
  meta,
  artistData,
  isFetchingTracks,
  isLoadingBenchmark,
}: PreReleaseTableBodyProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const isTrackDetailsEnabled = isEnvEnabled('VITE_WMG_TRACK_DETAILS_ENABLED');
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  if (isFetchingTracks) {
    return (
      <TableBody>
        <PreReleaseSkeletonTable
          size={12}
          headers={table.getHeaderGroups()[0].headers}
          lineClassName="h-lg rounded-md bg-level-05"
          cellClassName="pl-sm"
        />
      </TableBody>
    );
  }

  if (!table.getRowModel().rows?.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={6}>
            <DataUnavailable
              title={t('trackList.noData.title')}
              subtitle={t('trackList.noData.sub', {
                artistName: meta?.artistName || 'artist',
              })}
              icon={
                <Icon
                  type="turntable"
                  size="4x"
                  variant="solid"
                  aria-hidden
                  className="text-default"
                />
              }
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const handlePreReleaseTableRowClick = ({
    trackData,
  }: {
    trackData: AssetFamily;
  }) => {
    if (isTrackDetailsEnabled && meta && artistData) {
      trackEvent('artistPreReleaseListTrackClick', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
        track_name: trackData.title,
        family_id: trackData.id,
      });
    }
  };

  return (
    <TableBody>
      {table.getRowModel().rows.map((row, index) => {
        const rowId = row.original.id;
        const isRowHovered = hoveredRow === rowId;

        return (
          <TableRow
            aria-label={`Row for ${row.original?.id || 'track'}`}
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            className={
              isTrackDetailsEnabled
                ? cn(
                    'group/row relative transition-all duration-300 ease-out hover:bg-emphasis-subtle',
                    'hover:!opacity-full group-hover/table:opacity-stronger',
                  )
                : ''
            }
            onMouseEnter={() => setHoveredRow(rowId)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            {row.getVisibleCells().map((cell) => {
              const isTitleCell = cell.column.id === 'title';
              const isFirstWeekCell = cell.column.id === 'firstWeekStreams';

              const cellContent = flexRender(
                cell.column.columnDef.cell,
                cell.getContext(),
              );

              let content;

              if (
                BENCHMARK_COLUMNS.includes(cell.column.id) &&
                isLoadingBenchmark
              ) {
                /* @TODO WMG1-495 - Align loading state with UX design */
                content = (
                  <div className="space-y-sm pl-sm">
                    <Skeleton className="h-md w-3xl rounded-sm bg-level-04" />
                    <Skeleton className="h-md w-2xl rounded-sm bg-level-04" />
                  </div>
                );
              } else if (isTitleCell && isTrackDetailsEnabled) {
                content = (
                  <Link
                    to={`/roster/${meta.partyId}/${row.original.id}`}
                    className="flex items-center gap-sm py-sm pl-lg hover:underline"
                    onClick={() =>
                      handlePreReleaseTableRowClick({
                        trackData: row.original,
                      })
                    }
                  >
                    {cellContent}
                  </Link>
                );
              } else if (isFirstWeekCell) {
                content = (
                  <Tooltip
                    key={row.id}
                    content={<PreReleaseTooltipContent row={row} />}
                    contentClassName="rounded-sm border p-md text-body-sm bg-level-03 border-emphasis-subtle"
                    side="bottom"
                    sideOffset={16}
                    align="start"
                    size="sm"
                    triggerAsChild
                    delayDuration={0}
                    open={isRowHovered}
                  >
                    <div className="flex items-center gap-sm py-sm">
                      {cellContent}
                    </div>
                  </Tooltip>
                );
              } else {
                content = (
                  <div
                    className={cn('flex items-center gap-sm py-sm', {
                      'pl-lg': cell.column.id === 'title',
                      'justify-end pr-lg': cell.column.id === 'benchmarkAction',
                    })}
                  >
                    {cellContent}
                  </div>
                );
              }

              return (
                <TableCell
                  key={cell.id}
                  scope="row"
                  className={cn(
                    'border-y p-0 transition-all duration-300 ease-out border-transparent last:py-xs',
                    'group-hover/row:border-emphasis-strong',
                    'first:rounded-l-sm first:border-l',
                    'last:rounded-r-sm last:border-r last:pr-sm',
                    index % 2 === 0 && 'bg-default-inverse/default',
                  )}
                >
                  {content}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
}

PreReleaseTableBody.displayName = 'PreReleaseTableBody';
